/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject, Input, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { ChatComponent } from '@src/app/shared/chat/chat-component/chat.component';
import { ChatField } from '@src/app/shared/chat/chat-field';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { MyRequestService } from '@src/app/modules/crm/my-request.service';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FromNowPipe } from '@src/app/shared/pipes/from-now.pipe';
import { MatCardModule } from '@angular/material/card';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ReopenRequestComponent } from '@src/app/modules/crm/service-request-page/reopen-request/reopen-request.component';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MyInjector } from '@src/app/app.module';
import { RequestService } from '@src/app/modules/crm/request.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { Role } from '@src/app/model/role';
import { DateHelper } from '@src/app/shared/dateHelper';
import { uuid } from '@src/app/model/abstract-object';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { SrTopic } from '@src/app/model/sr-topic';
import { SrClass } from '@src/app/model/sr-class';
import { SrTopicService } from '@src/app/modules/crm/sr-topic.service';
import { SrClassService } from '@src/app/modules/crm/sr-class.service';

@Component({
    selector: 'app-my-request',
    templateUrl: './my-request.component.html',
    styleUrls: ['./my-request.component.scss'],
    imports: [CtlHolderComponent, FromNowPipe, MatCardModule,
        RouterLink, NgTemplateOutlet, MatButtonModule, NgClass],
    providers: [DatePipe]
})
export class MyRequestComponent {
    private myRequestService = inject(MyRequestService);
    private cds = inject(ConfirmDialogService);
    private cus = inject(CurrentUserService);
    private srTopicSvc = inject(SrTopicService);
    private srClassSvc = inject(SrClassService);

    // Server reference data - used only to show the topic/class names read-only.
    srTopics: SrTopic[] = [];
    srClasses: SrClass[] = [];


    @Input() request: ServiceRequest;
    dialog = inject(MatDialog);
    activatedRoute = inject(ActivatedRoute);
    router = inject(Router);
    datePipe = inject(DatePipe);

    fullRequest: ServiceRequest;
    descriptionText: string;

    isPhone = false;
    formHeight = this.getFormHeight();

    chatField: ChatField = ChatComponent.make(this.myRequestService).hideFollowers()
        .refreshAfterComment( () => this.loadRequest(this.fullRequest.id));

    attachmentGrid = AttachmentGridComponent.make(this.myRequestService).hideGrid();

    openRequests : ServiceRequest[]= [];
    isUnitOwnerRequest = false;

    private destroyRef = inject(DestroyRef);

    constructor() {
        const isNarrowSvc = inject(IsNarrowService);
        const cus = this.cus;

        isNarrowSvc.detectVeryNarrow().pipe(takeUntilDestroyed()).subscribe(result => { this.isPhone = result; });
        isNarrowSvc.resizeObservable$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.formHeight = this.getFormHeight();
        })
        this.srTopicSvc.get<SrTopic>(false).subscribe(topics => { this.srTopics = topics; });
        this.srClassSvc.get<SrClass>(false).subscribe(classes => { this.srClasses = classes; });
        this.chatField.makeControl();
        this.attachmentGrid.makeControl();
        this.attachmentGrid.showAllFields = false;
        this.attachmentGrid.allowSimpleAdd = true;
        this.activatedRoute.params.pipe(takeUntilDestroyed()).subscribe( parms => {
            cus.getCurrentUser().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(u => {
                if (u?.inAgency) {
                    this.router.navigate([ServiceRequestPageNavRoute.getListRouteName(), parms.itemId], { replaceUrl: true });
                } else {
                    this.loadRequest(parms.itemId);
                }
            })
        })
    }

    loadRequest(requestId: uuid) {
        this.myRequestService.getOne(requestId, null).subscribe(fullRequest => {
            if (fullRequest) {
                this.fullRequest = fullRequest;
                this.descriptionText = this.replaceAll(fullRequest.description, String.fromCharCode(10), '<br/>');
                this.chatField.setValue(fullRequest, false);
                this.attachmentGrid.setValue(fullRequest, false);
                if (this.fullRequest.srStatus === ServiceRequest.srOpen.id) {
                    this.chatField.allowComments();
                    this.attachmentGrid.setReadonly(false);
                } else {
                    this.chatField.disableComments();
                    this.attachmentGrid.setReadonly(true);
                }
                this.checkForUnitOwner();
            }
            this.myRequestService.get(true).subscribe((r: ServiceRequest[]) => {
                this.openRequests = r.filter(o => o.srStatus === ServiceRequest.srOpen.id);
            })
        });
    }

    checkForUnitOwner() {
        this.isUnitOwnerRequest = false;
        const ap = this.cus.getActivePositions();
        const accessRequestFor = (this.fullRequest?.data ? this.fullRequest?.data['person'] : null);
        if (accessRequestFor) {
            if (accessRequestFor.unit?.resident && !accessRequestFor.unit?.owner) {
                const apu = ap.filter(p => p.unitId === this.fullRequest.unitId);
                apu.forEach( pur => {
                    if  (pur.role.globalRoleId === Role.ROLE.OWNER.id
                    || pur.role.globalRoleId === Role.ROLE.OWNER_OCCUPIER.id
                    || pur.role.globalRoleId === Role.ROLE.LETTING_AGENT.id) {
                        this.isUnitOwnerRequest = true;
                    }
                })
            }
        }
    }
    canDeactivate() {
        return true;
    }

    getCloseDate(dt) {
        const closeDate = DateHelper.unixDatePlusDays(dt, 7);
        return this.datePipe.transform(closeDate, 'fullDate');
    }

    replaceAll(str, find, replace) {
        return str.replace(new RegExp(find, 'g'), replace);
    }

    getFormHeight() {
        return window.innerHeight - 276;
    }

    requestStatus() {
        if (this.fullRequest) {
            return ServiceRequest.srStates.find(o => this.fullRequest.srStatus === o.id).name;
        }
        return ''
    }

    requestSubStatus() {
        if (this.fullRequest?.srCloseStatus) {
            return ServiceRequest.srSubStates.find(o => this.fullRequest.srCloseStatus === o.id).name;
        }
        return ''
    }

    requestSource() {
        const source = ServiceRequest.requestSources.find(o => o.id === this.fullRequest?.requestSource);
        return source ? source.name : '';
    }

    requestTopic() {
        return this.srTopics.find(o => o.id === this.fullRequest?.srTopic)?.name ?? '';
    }

    requestClass() {
        return this.srClasses.find(o => o.id === this.fullRequest?.srClass)?.name ?? '';
    }
    addToUnit() {
        this.cds.open('Add Person as resident',
        'Add this person now as a resident of your unit, granting them resident level access to the system?',
        () => {
            this.myRequestService.approveResidentAccess(this.fullRequest).subscribe( r => {
                if (r) {
                    this.fullRequest = r;
                    this.chatField.setValue(r, false);
                    this.chatField.disableComments();
                    this.attachmentGrid.setReadonly(true);
                }
            })
        })
    }
    declineAddToUnit() {
        const person = this.fullRequest.data['person'];
        this.cds.open('Do not add person to unit',
        `Let the management team know you do not want this person added to your unit?
            Your answer is added to the request, where ${person.firstName} ${person.lastName} can also see it.`,
        () => {
            const content = `I do not approve ${person.firstName} ${person.lastName} being added to this unit.`;
            this.myRequestService.postChatItem({ content }, this.fullRequest.id).subscribe(() => {
                this.loadRequest(this.fullRequest.id);
            })
        })
    }
    reopenRequest() {
        this.dialog.open(ReopenRequestComponent, { data: this.fullRequest }).afterClosed().subscribe(r => {
            if (r) {
                this.fullRequest = r;
                this.chatField.setValue(r, false);
                this.chatField.allowComments();
                this.attachmentGrid.setReadonly(false);
            }
        })
    }
    acceptRequest() {
        const t = $localize `Accept Request Is Completed`;
        const m = $localize `Click OK to accept request is complete.
            It will no longer be possible to reopen or add comments to it.`
        const cds = MyInjector.instance.get(ConfirmDialogService);
        cds.open(t,m, () => {
            const rs = MyInjector.instance.get(RequestService);
            rs.acceptRequest(this.fullRequest).subscribe(r => {
                this.fullRequest = r;
            });
        })
    }
}
